import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { esitoUpload } from "@/lib/upload";
import UploadDialog from "@/components/UploadDialog";

const ANAGRAFICA_CAT = "Documenti Cliente";
const GENERI = ["Contratti", "Privacy", "Deleghe", "Ricevute", "Altro"];
const stripExt = (n) => (n || "").replace(/\.[^.]+$/, "");
const dataIt = (s) => (s ? new Date(s).toLocaleDateString("it-IT") : "");

// Zona di caricamento, a tutta larghezza sopra le due colonne.
export function ZonaCaricamento({ cliente, testo, classe = "", soloBottone }) {
  const qc = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["drive-files", cliente?.id] });

  const invia = async (list) => {
    const scelti = Array.from(list || []);
    if (!scelti.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      scelti.forEach((f) => { fd.append("files", f); fd.append("names", stripExt(f.name)); });
      const { data: res } = await api.post(`/clienti/${cliente.id}/upload`, fd);
      toast.success(esitoUpload(res, "Documenti caricati"));
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Caricamento non riuscito"); }
    finally { setBusy(false); }
  };

  if (soloBottone) {
    return (
      <>
        <button data-testid="carica-un-file" onClick={() => setUploadOpen(true)} disabled={busy}
          className="btn btn-o btn-larghezza">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Carico…</> : "Carica un file"}
        </button>
        {uploadOpen && cliente && (
          <UploadDialog cliente={cliente} titolo="Carica documenti" categoriaOptions={[ANAGRAFICA_CAT, ...GENERI]}
            onClose={() => setUploadOpen(false)} onUploaded={refresh} />
        )}
      </>
    );
  }

  return (
    <>
      <div data-testid="carica-documenti-area" className={`zona-file ${classe}`}
        style={drag ? { borderColor: "#A8863F", background: "#FFFFFF" } : undefined}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); invia(e.dataTransfer.files); }}>
        <span className="text-[14px] text-[#4A4A48] flex-[999_1_200px]">
          {testo || "Trascina qui i file, oppure scegli dove metterli: documenti del cliente o della pratica."}
        </span>
        <button data-testid="carica-documenti-scegli" onClick={() => setUploadOpen(true)} disabled={busy}
          className="btn btn-o btn-larghezza sm:w-auto">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Carico…</> : "Scegli file"}
        </button>
      </div>
      {uploadOpen && cliente && (
        <UploadDialog cliente={cliente} titolo="Carica documenti" categoriaOptions={[ANAGRAFICA_CAT, ...GENERI]}
          onClose={() => setUploadOpen(false)} onUploaded={refresh} />
      )}
    </>
  );
}

// Colonna di sinistra della sezione Documenti: i file già su Drive.
export default function PraticaDocsBox({ cliente, documentiGenerati = [], compatto, titolo = "Caricati" }) {
  const clienteId = cliente?.id;
  const { data, isLoading } = useQuery({
    queryKey: ["drive-files", clienteId],
    queryFn: async () => (await api.get(`/clienti/${clienteId}/drive-files`)).data,
    enabled: !!clienteId,
  });
  const generati = new Set(documentiGenerati.map((d) => d.drive_link).filter(Boolean));
  const files = (data?.files || []).filter((f) => !generati.has(f.webViewLink));
  const connected = data?.connected;

  return (
    <div className={compatto ? "min-w-0" : "sez min-w-0"} data-testid="documenti-box">
      <div className="subt">
        <span>{titolo}</span>
        <span className="text-[13px] font-normal normal-case tracking-normal text-[#5A5A5A]">
          {files.length} file
        </span>
      </div>
      <div className="max-h-[320px] overflow-y-auto">
        {!connected && <div className="riga text-[#8A2E2E]">Collega Google in Impostazioni per vedere i documenti.</div>}
        {connected && isLoading && <div className="riga text-[#5A5A5A]"><Loader2 className="w-4 h-4 animate-spin" /> Carico i documenti…</div>}
        {connected && !isLoading && files.length === 0 && (
          <div className="riga text-[#5A5A5A]" data-testid="documenti-vuoti">Nessun documento caricato.</div>
        )}
        {files.map((f) => (
          <div key={f.id} data-testid="pratica-drive-doc" className="riga">
            <span className="flex-grow min-w-0 truncate"
              title={`${f.name}${f.categoria ? ` · ${f.categoria}` : ""}${f.modifiedTime ? ` · ${dataIt(f.modifiedTime)}` : ""}`}>
              {f.name}
            </span>
            <a href={f.webViewLink} target="_blank" rel="noreferrer"
              className="text-[13px] font-semibold no-underline shrink-0">Apri</a>
          </div>
        ))}
      </div>
    </div>
  );
}
